import * as Sentry from "@sentry/react";

/**
 * Production error reporting.
 *
 * Initializes Sentry when a DSN is provided via VITE_SENTRY_DSN
 * (configured in Workspace Settings → Build Secrets). Safe no-op in
 * development or when the DSN is missing — local crashes still surface
 * via the in-app ErrorBoundary console output.
 */

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = (import.meta.env.MODE ?? "production") as string;
// __BUILD_ID__ is injected by vite.config.ts → define
// (kept loose to avoid TS complaints if the global is missing in tests).
const RELEASE =
  typeof (globalThis as Record<string, unknown>).__BUILD_ID__ === "string"
    ? ((globalThis as Record<string, unknown>).__BUILD_ID__ as string)
    : undefined;

let initialized = false;

export function initSentry() {
  if (initialized) return;
  if (!DSN) {
    // Dev or unconfigured — keep things quiet.
    return;
  }
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    // Global error-event sample rate: capture 100% of actual crashes.
    // This is separate from performance trace sampling below.
    sampleRate: 1.0,
    // Performance: keep very low to avoid quota burn; only bump when
    // actively debugging a perf regression.
    tracesSampleRate: 0.01,
    // Replay: record only when an error happens, never random sessions.
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    // ---- Noise reduction: ignore known non-actionable patterns ----
    ignoreErrors: [
      // Browser / UI noise
      /ResizeObserver loop/i,
      /Non-Error promise rejection captured/i,
      // Stale Vite chunks — handled by auto-reload in main.tsx
      /Importing a module script failed/i,
      /Failed to fetch dynamically imported module/i,
      /error loading dynamically imported module/i,
      // Network noise we cannot control
      /AbortError/i,
      /CanceledError/i,
      /cancelled/i,
      /Network Error/i,
      /NetworkError/i,
      /Failed to fetch/i,
      /fetch aborted/i,
      /The operation was aborted/i,
      /timeout/i,
      // Third-party / extension noise
      /chrome-extension/i,
      /moz-extension/i,
      /safari-extension/i,
      /safari-web-extension/i,
      /cannot read property 'dispose' of null/i, // common extension artifact
      /Script error\.?/i, // cross-origin script errors with no actionable stack
      /window.onerror called with an Invalid event/i,
      // React development / hydration chatter (shouldn't fire in prod, but guards)
      /Hydration failed/i,
      /Minified React error/i,
    ],
    // Ignore entire files/URLs we cannot control
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:/i,
      /^moz-extension:/i,
      /^safari-extension:/i,
      /^safari-web-extension:/i,
      // Facebook / social widgets
      /connect\.facebook\.net/i,
      /graph\.facebook\.com/i,
      /fbcdn\.net/i,
      // Common analytics / ad scripts
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
      /googleadservices\.com/i,
      /doubleclick\.net/i,
      // Third-party embeds
      /youtube\.com\/embed/i,
      /player\.vimeo\.com/i,
      /instagram\.com\/embed/i,
    ],
    beforeSend(event, hint) {
      // Defensive: drop if DSN missing.
      if (!DSN) return null;

      const original = hint?.originalException;
      const message =
        typeof original === "string"
          ? original
          : original instanceof Error
            ? original.message
            : "";

      // Extra guard: drop events from browser extensions even if they slip
      // past ignoreErrors (e.g. wrapped errors).
      if (
        typeof message === "string" &&
        (/chrome-extension|moz-extension|safari-extension/.test(message) ||
          /ResizeObserver loop limit exceeded/.test(message))
      ) {
        return null;
      }

      // Drop fetch errors for 4xx client errors (our code isn't buggy, user
      // is just offline / authed out / rate-limited). Keep 5xx and network
      // failures so we know when the backend or CDN misbehaves.
      const lastException = event.exception?.values?.[ 1 ];
      if (
        lastException?.mechanism?.type === "onunhandledrejection" &&
        /fetch|network/i.test(message)
      ) {
        const status = (hint?.syntheticException as any)?.status;
        if (typeof status === "number" && status >= 400 && status < 500) {
          return null;
        }
      }

      return event;
    },
  });
  initialized = true;
}

export function reportError(error: unknown, context?: Record<string, unknown>) {
  if (!initialized) {
    // audit-console-warnings: allow
    console.error("[reportError] (sentry off)", error, context); // eslint-disable-line no-console
    return;
  }
  Sentry.captureException(error, { extra: context });
}

/**
 * Safely add a Sentry breadcrumb. No-op when Sentry is not initialized
 * (dev or missing DSN) so callers never need to guard.
 */
export function addSentryBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  if (!initialized) return;
  Sentry.addBreadcrumb(breadcrumb);
}

export { Sentry };